import { t as coreT } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { DEFAULT_RECORDER_HOST, DEFAULT_RECORDER_PORTS, RECORDER_KINDS } from 'modules/video-markers/domain/constants';
import { RecorderConfig, RecorderKind } from 'modules/video-markers/domain/interfaces';
import { Pages } from 'modules/navigation/domain/enums/pages';
import UDLink from 'ud-ui/components/link';
import UDFormInput from 'ud-form/components/input';
import { SelectOption } from 'ud-form/components/select/component';
import UDFormSelect from 'ud-form/components/select/ud-form-select';
import { UDButton, UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';

/** `none` — видеометки выключены: фрагмента в ссылке нет, оверлей ведёт себя как без них. */
type RecorderChoice = RecorderKind | 'none';

export interface RecorderSettingsFormValues {
  recorder: SelectOption<RecorderChoice>;
  host: string;
  /** Строкой: поле ввода отдаёт текст, а `abc` должен дойти до валидации, а не стать `NaN`. */
  port: string;
  password: string;
}

const RECORDER_TITLES: Record<RecorderKind, string> = { obs: 'OBS', vmix: 'vMix' };

/** Связывают подписи с полями: обёртка `ud-form` сама `htmlFor` не ставит. */
const FIELD_IDS = {
  recorder: 'video-markers-recorder',
  host: 'video-markers-host',
  port: 'video-markers-port',
  password: 'video-markers-password',
} as const;

const validationSchema = yup.object().shape({
  host: yup.string().when('recorder', {
    is: (recorder: SelectOption<RecorderChoice>) => recorder.value !== 'none',
    then: (schema) => schema.trim().required(() => coreT`Обязательное поле`),
  }),
  port: yup.string().when('recorder', {
    is: (recorder: SelectOption<RecorderChoice>) => recorder.value !== 'none',
    then: (schema) =>
      schema
        .required(() => coreT`Обязательное поле`)
        .test(
          'port',
          () => coreT`Порт — число от 1 до 65535`,
          (value) => isValidPort(value),
        ),
  }),
});

export interface RecorderSettingsModalProps {
  /** Текущая настройка; `null` — видеометки выключены. */
  value: RecorderConfig | null;
  onSave: (value: RecorderConfig | null) => void;
  onClose: () => void;
}

/**
 * Подключение оверлея к OBS или vMix за позицией записи. Настройка уезжает в ссылку на табло
 * `#`-фрагментом (`recorder-config-fragment.ts`), поэтому после правки ссылку копируют заново.
 * Монтировать только на время показа — закрытие сбрасывает несохранённые поля.
 */
function RecorderSettingsModal({ value, onSave, onClose }: RecorderSettingsModalProps) {
  const { t } = useLingui();

  const options = useMemo<SelectOption<RecorderChoice>[]>(
    () => [{ value: 'none', label: t`Выключены` }, ...RECORDER_KINDS.map((kind) => ({ value: kind, label: RECORDER_TITLES[kind] }))],
    [t],
  );

  const initialValues = useMemo<RecorderSettingsFormValues>(
    () => ({
      recorder: options.find((option) => option.value === (value?.recorder ?? 'none'))!,
      host: value?.host ?? DEFAULT_RECORDER_HOST,
      port: value ? String(value.port) : '',
      password: value?.password ?? '',
    }),
    [options, value],
  );

  const handleSubmit = useCallback(
    // Сохранение синхронное — `isSubmitting` сбрасываем сами, иначе Formik оставит кнопку запертой (IMGM-1561).
    (values: RecorderSettingsFormValues, { setSubmitting }: FormikHelpers<RecorderSettingsFormValues>) => {
      onSave(toRecorderConfig(values));
      setSubmitting(false);
      onClose();
    },
    [onSave, onClose],
  );

  return (
    <Modal show onClose={onClose} size="md" noOverflow>
      <Modal.Header
        title={t`Видеометки`}
        subtitle={t`Оверлей подключится к программе трансляции и отметит, на какой секунде записи случилось каждое событие матча.`}
      />

      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ values, setFieldValue, validateForm, isSubmitting }) => {
          const recorder = values.recorder.value;

          // Порт по умолчанию у OBS и vMix разный: при смене программы подставляем её порт,
          // если поле пустое или в нём порт другой программы — свой порт оператора не трогаем.
          const changeRecorder = (option: SelectOption<RecorderChoice> | null) => {
            if (!option || option.value === 'none') return;
            const isDefaultPort = values.port === '' || Object.values(DEFAULT_RECORDER_PORTS).map(String).includes(values.port);
            if (!isDefaultPort) return;

            setFieldValue('port', String(DEFAULT_RECORDER_PORTS[option.value]), false);
            // Formik 2.2.9 валидирует `setFieldValue` по `values` прошлой отрисовки: проверка смены
            // программы видит ещё пустой порт и ставит «Обязательное поле». Перепроверяем форму, когда
            // оба значения уже в состоянии, — макрозадача идёт после всех промисов тех проверок.
            setTimeout(() => validateForm());
          };

          return (
            <Form>
              <Modal.Body className="flex flex-col gap-4">
                <UDFormSelect
                  name="recorder"
                  label={t`Программа`}
                  labelProps={{ htmlFor: FIELD_IDS.recorder }}
                  inputId={FIELD_IDS.recorder}
                  options={options}
                  onChange={changeRecorder}
                />

                {recorder !== 'none' && (
                  <div className="flex gap-4">
                    <UDFormInput
                      name="host"
                      id={FIELD_IDS.host}
                      label={t`Адрес`}
                      labelProps={{ htmlFor: FIELD_IDS.host }}
                      placeholder={DEFAULT_RECORDER_HOST}
                      errorVisible
                    />
                    <UDFormInput
                      name="port"
                      id={FIELD_IDS.port}
                      label={t`Порт`}
                      labelProps={{ htmlFor: FIELD_IDS.port }}
                      inputMode="numeric"
                      placeholder={String(DEFAULT_RECORDER_PORTS[recorder])}
                      containerProps={{ className: 'basis-36 shrink-0 grow-0' }}
                      errorVisible
                    />
                  </div>
                )}

                {recorder === 'obs' && (
                  <>
                    <UDFormInput
                      name="password"
                      id={FIELD_IDS.password}
                      type="password"
                      label={t`Пароль сервера WebSocket`}
                      labelProps={{ htmlFor: FIELD_IDS.password }}
                      autoComplete="new-password"
                      errorVisible
                    />
                    <UDText variant="body-3" className="text-inkSecondary">
                      <Trans>Пароль останется только в скопированной ссылке на табло и на сервер не отправляется.</Trans>
                    </UDText>
                  </>
                )}

                <UDText variant="body-3" className="text-inkSecondary">
                  <Trans>После сохранения скопируйте ссылку на табло заново и вставьте её в программу.</Trans>
                </UDText>

                <UDLink to={Pages.DEVICES.VIDEO_MARKERS_GUIDE} type="footnote">
                  <Trans>Как настроить OBS и vMix</Trans>
                </UDLink>
              </Modal.Body>

              <Modal.Footer>
                <UDButton variant="secondary" type="button" onClick={onClose}>
                  <Trans>Отмена</Trans>
                </UDButton>
                <UDButton variant="primary" type="submit" loading={isSubmitting}>
                  <Trans>Сохранить</Trans>
                </UDButton>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

function isValidPort(value: string | undefined): boolean {
  if (!value || !/^\d+$/.test(value)) return false;

  const port = Number(value);
  return port >= 1 && port <= 65535;
}

function toRecorderConfig(values: RecorderSettingsFormValues): RecorderConfig | null {
  const recorder = values.recorder.value;
  if (recorder === 'none') return null;

  const config: RecorderConfig = { recorder, host: values.host.trim(), port: Number(values.port) };
  if (recorder === 'obs' && values.password) config.password = values.password;

  return config;
}

export default React.memo(RecorderSettingsModal);
